import { getPointAtAngle } from './geometry';

function buildChildrenByParent(relationships) {
  const childrenByParent = new Map();
  (relationships || []).forEach((relationship) => {
    if (!relationship?.parentId || !relationship?.childId) return;
    const children = childrenByParent.get(relationship.parentId) || [];
    children.push(relationship.childId);
    childrenByParent.set(relationship.parentId, children);
  });
  return childrenByParent;
}

function collectBranchNodeIds(rootId, childrenByParent) {
  if (!rootId) return new Set();
  const branch = new Set([rootId]);
  const pending = [rootId];

  while (pending.length) {
    const parentId = pending.pop();
    (childrenByParent.get(parentId) || []).forEach((childId) => {
      if (branch.has(childId)) return;
      branch.add(childId);
      pending.push(childId);
    });
  }
  return branch;
}

export function getBranchNodeIds(rootId, relationships) {
  const childrenByParent = buildChildrenByParent(relationships);
  return collectBranchNodeIds(rootId, childrenByParent);
}

export function getAncestorNodeIds(nodeId, relationships) {
  const parentByChild = new Map();
  (relationships || []).forEach((relationship) => {
    if (relationship?.parentId && relationship?.childId) {
      parentByChild.set(relationship.childId, relationship.parentId);
    }
  });

  const ancestors = new Set();
  let currentId = nodeId;
  while (parentByChild.has(currentId)) {
    const parentId = parentByChild.get(currentId);
    if (!parentId || ancestors.has(parentId) || parentId === nodeId) break;
    ancestors.add(parentId);
    currentId = parentId;
  }
  return ancestors;
}

export function getNodePath(nodeId, relationships) {
  if (!nodeId) return [];
  const parentByChild = new Map();
  (relationships || []).forEach((relationship) => {
    if (relationship?.parentId && relationship?.childId) {
      parentByChild.set(relationship.childId, relationship.parentId);
    }
  });

  const path = [nodeId];
  const visited = new Set(path);
  let currentId = nodeId;
  while (parentByChild.has(currentId)) {
    const parentId = parentByChild.get(currentId);
    if (!parentId || visited.has(parentId)) break;
    path.push(parentId);
    visited.add(parentId);
    currentId = parentId;
  }

  return path.reverse();
}

export function deriveVisibleNodeIds({
  nodes,
  relationships,
  focusRootId = null,
  collapsedNodeIds = new Set(),
  temporarilyExpandedNodeIds = new Set(),
  excludedNodeIds = new Set(),
}) {
  const validNodeIds = new Set((nodes || []).map((node) => node?.id).filter(Boolean));
  const childrenByParent = buildChildrenByParent(relationships);
  const visible = focusRootId && validNodeIds.has(focusRootId)
    ? collectBranchNodeIds(focusRootId, childrenByParent)
    : new Set(validNodeIds);

  collapsedNodeIds.forEach((collapsedId) => {
    if (temporarilyExpandedNodeIds.has(collapsedId)) return;
    if (!visible.has(collapsedId)) return;
    const hiddenBranch = collectBranchNodeIds(collapsedId, childrenByParent);
    hiddenBranch.delete(collapsedId);
    hiddenBranch.forEach((nodeId) => visible.delete(nodeId));
  });

  excludedNodeIds.forEach((nodeId) => visible.delete(nodeId));

  return new Set([...visible].filter((nodeId) => validNodeIds.has(nodeId)));
}

export function countHiddenDescendants(nodeId, relationships, visibleNodeIds) {
  const branch = getBranchNodeIds(nodeId, relationships);
  branch.delete(nodeId);
  let count = 0;
  branch.forEach((descendantId) => {
    if (!visibleNodeIds.has(descendantId)) count += 1;
  });
  return count;
}

export function deriveCollapsedNodeIdsForDepth({
  rootId,
  relationships,
  collapsedNodeIds = new Set(),
  maxDepth = null,
}) {
  const childrenByParent = buildChildrenByParent(relationships);
  const branchNodeIds = collectBranchNodeIds(rootId, childrenByParent);
  const nextCollapsedNodeIds = new Set(collapsedNodeIds || []);

  // A level command replaces only the collapse state inside the current
  // map/focused branch. Personal collapse choices elsewhere are preserved.
  branchNodeIds.forEach((nodeId) => nextCollapsedNodeIds.delete(nodeId));
  if (!Number.isFinite(maxDepth)) return nextCollapsedNodeIds;

  const depthLimit = Math.max(0, Math.floor(maxDepth));
  const depthByNodeId = new Map();
  const pending = [{ nodeId: rootId, depth: 0 }];
  let pendingIndex = 0;
  while (pendingIndex < pending.length) {
    const { nodeId, depth } = pending[pendingIndex];
    pendingIndex += 1;
    if (!nodeId || depthByNodeId.has(nodeId)) continue;
    depthByNodeId.set(nodeId, depth);
    (childrenByParent.get(nodeId) || []).forEach((childId) => {
      if (!depthByNodeId.has(childId)) {
        pending.push({ nodeId: childId, depth: depth + 1 });
      }
    });
  }

  depthByNodeId.forEach((depth, nodeId) => {
    if (depth !== depthLimit) return;
    const hasDeeperChild = (childrenByParent.get(nodeId) || []).some(
      (childId) => depthByNodeId.get(childId) === depth + 1
    );
    if (hasDeeperChild) nextCollapsedNodeIds.add(nodeId);
  });

  return nextCollapsedNodeIds;
}

export function detectBranchRevealDepth({
  rootId,
  relationships,
  collapsedNodeIds = new Set(),
  presets = [1, 2, 3],
}) {
  const branchNodeIds = getBranchNodeIds(rootId, relationships);
  const hasCollapsedBranch = [...(collapsedNodeIds || [])].some((nodeId) => (
    branchNodeIds.has(nodeId)
  ));
  if (!hasCollapsedBranch) return 'all';

  const current = new Set(collapsedNodeIds || []);
  const setsMatch = (left, right) => (
    left.size === right.size && [...left].every((nodeId) => right.has(nodeId))
  );

  for (const preset of presets) {
    const expected = deriveCollapsedNodeIdsForDepth({
      rootId,
      relationships,
      collapsedNodeIds: current,
      maxDepth: preset,
    });
    if (setsMatch(current, expected)) return String(preset);
  }

  return 'custom';
}

export function restoreLegacyCollapsedMap(nodes, relationships) {
  const hasLegacyContent = (nodes || []).some((node) => (
    Array.isArray(node?.collapsedChildren?.nodes)
    && node.collapsedChildren.nodes.length > 0
  ));
  if (!hasLegacyContent) {
    return {
      nodes: Array.isArray(nodes) ? nodes : [],
      relationships: Array.isArray(relationships) ? relationships : [],
    };
  }

  const nodeMap = new Map();
  const pending = [];
  (nodes || []).forEach((node) => {
    if (!node?.id) return;
    nodeMap.set(node.id, { ...node, collapsedChildren: null });
    pending.push(node);
  });

  const relationshipMap = new Map();
  (relationships || []).forEach((relationship) => {
    if (relationship?.parentId && relationship?.childId) {
      relationshipMap.set(`${relationship.parentId}:${relationship.childId}`, relationship);
    }
  });

  while (pending.length) {
    const parentSnapshot = pending.pop();
    const collapsed = parentSnapshot?.collapsedChildren;
    if (!collapsed || !Array.isArray(collapsed.nodes)) continue;
    const parent = nodeMap.get(parentSnapshot.id);
    if (!parent?.position) continue;

    collapsed.nodes.forEach((childSnapshot) => {
      if (!childSnapshot?.id || nodeMap.has(childSnapshot.id)) return;
      const relative = childSnapshot.position;
      const position = Number.isFinite(relative?.angle) && Number.isFinite(relative?.distance)
        ? getPointAtAngle(
            parent.position.x,
            parent.position.y,
            relative.angle,
            relative.distance
          )
        : relative;
      nodeMap.set(childSnapshot.id, {
        ...childSnapshot,
        position,
        collapsedChildren: null,
      });
      pending.push(childSnapshot);
    });

    (collapsed.relationships || []).forEach((relationship) => {
      if (relationship?.parentId && relationship?.childId) {
        relationshipMap.set(`${relationship.parentId}:${relationship.childId}`, relationship);
      }
    });
  }

  return {
    nodes: [...nodeMap.values()],
    relationships: [...relationshipMap.values()],
  };
}
